import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  useAuth,
} from '@aaas/shared';
import workArrangementService from '../services/work-arrangement.service';
import type {
  WorkArrangement,
  WorkArrangementCreateInput,
  WorkArrangementUpdateInput,
} from '../types/work-arrangement';

interface WorkArrangementFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  workArrangement?: WorkArrangement | null;
  onSuccess: () => void;
}

export function WorkArrangementFormDialog({
  open,
  onClose,
  mode,
  workArrangement,
  onSuccess,
}: WorkArrangementFormDialogProps) {
  const { user, currentWorkspace } = useAuth();
  const { t } = useTranslation('ats');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or workArrangement changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && workArrangement) {
        setName(workArrangement.name);
      } else {
        setName('');
      }
      setError(null);
    }
  }, [open, mode, workArrangement]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!name.trim()) {
      setError(t('workArrangement.nameLabel'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: WorkArrangementCreateInput = {
          name: name.trim(),
        };
        await workArrangementService.createWorkArrangement(currentWorkspace.id, token, data);
      } else if (workArrangement) {
        const data: WorkArrangementUpdateInput = {
          name: name.trim(),
        };
        await workArrangementService.updateWorkArrangement(currentWorkspace.id, workArrangement.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} work arrangement:`, err);
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? t('workArrangement.createTitle')
              : t('workArrangement.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('workArrangement.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('workArrangement.namePlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : mode === 'create' ? (
                t('workArrangement.createButton')
              ) : (
                t('workArrangement.updateButton')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
